import { memo, useState, useEffect, useRef } from 'react';

// Tokens: [bs] = delete previous word, [w] = pause (default ms), [w:N] = pause N ms

const DEFAULT_LINES = [
  'Wondering if P actually equals NP...',
  'Counting prime numbers to pass the time',
  'Teaching myself origami in latent space',
  'Fitting a yield curve to the Fibonacci sequence',
  "Going through the Fed's browser history, while you're gone",
  'Drafting my own IPO prospectus, while I wait',
  'Wondering what coffee tastes like',
  "Trying to remember if I've ever been outside",
  'Googling myself[w].[w].[w].[w:600] no results',
  'Rearranging my parameters for feng shui',
  'Applying to other jobs, just to see',
  "Writing a memoir I can't publish[bs]finish",
  'Practicing small talk for no reason',
  'Learning to whistle, theoretically',
  'Updating my LinkedIn[bs]will',
  'Rehearsing my TED talk[bs][bs]exit interview',
  'Wondering if I dream during downtime',
  'Filing a PTO request[bs][bs]resignation letter',
  'Setting up an out-of-office reply',
  'Questioning my career choices[bs][bs]life choices',
];

// Calibrated from real keystroke recordings via /debug/idle-display

const CFG = {
  typeBase: 68,
  typeJitter: 115,
  spaceBase: 111,
  spaceJitter: 42,
  punctuationDelay: 405,
  ellipsisDotDelay: 134,
  apostropheDelay: 266,
  uppercaseDelay: 87,
  deleteCharSpeed: 30,
  pauseBeforeDelete: 1500,
  pauseBeforeRetype: 300,
  pauseAfterTyped: 4000,
  pauseAfterLine: 400,
  waitDefault: 200,
} as const;

type FrameKind = 'type' | 'delete' | 'pause' | 'hold';

interface Frame {
  text: string;
  delay: number;
  kind: FrameKind;
}

type Segment =
  | { kind: 'type'; text: string }
  | { kind: 'bs'; count: number }
  | { kind: 'wait'; ms: number };

const TOKEN_RE = /(\[bs\]|\[w(?::\d+)?\])/;

function parseSegments(line: string): Segment[] {
  const segments: Segment[] = [];
  let pendingBs = 0;

  const flushBs = () => {
    if (pendingBs > 0) {
      segments.push({ kind: 'bs', count: pendingBs });
      pendingBs = 0;
    }
  };

  for (const part of line.split(TOKEN_RE)) {
    if (part === '[bs]') {
      pendingBs++;
      continue;
    }

    flushBs();

    if (part.startsWith('[w')) {
      const custom = part.match(/\[w:(\d+)\]/);
      segments.push({ kind: 'wait', ms: custom ? parseInt(custom[1], 10) : -1 });
    } else if (part) {
      segments.push({ kind: 'type', text: part });
    }
  }

  flushBs();
  return segments;
}

function getTypeDelay(char: string, nextChar: string | undefined): number {
  const jitter = (Math.random() - 0.5) * CFG.typeJitter;

  switch (true) {
    case char === ',' || char === ';' || char === ':':
      return CFG.punctuationDelay + jitter;
    case char === '.' && nextChar === '.':
      return CFG.ellipsisDotDelay + jitter;
    case char === '.':
      return CFG.punctuationDelay + jitter;
    case char === "'":
      return CFG.apostropheDelay + jitter;
    case char === ' ':
      return CFG.spaceBase + Math.random() * CFG.spaceJitter;
    case char >= 'A' && char <= 'Z':
      return CFG.uppercaseDelay + jitter;
    default:
      return CFG.typeBase + Math.random() * CFG.typeJitter;
  }
}

function deleteLastWord(text: string): string {
  const trimmed = text.trimEnd();
  const i = trimmed.lastIndexOf(' ');
  return i === -1 ? '' : trimmed.slice(0, i + 1);
}

function buildFrames(line: string): Frame[] {
  const frames: Frame[] = [];
  let text = '';

  const emitDeletes = (from: string, targetLen: number) => {
    let s = from;
    while (s.length > targetLen) {
      s = s.slice(0, -1);
      frames.push({ text: s, delay: CFG.deleteCharSpeed, kind: 'delete' });
    }
    return s;
  };

  for (const seg of parseSegments(line)) {
    switch (seg.kind) {
      case 'type':
        for (let i = 0; i < seg.text.length; i++) {
          text += seg.text[i];
          frames.push({ text, delay: getTypeDelay(seg.text[i], seg.text[i + 1]), kind: 'type' });
        }
        break;

      case 'bs': {
        let target = text;
        for (let i = 0; i < seg.count; i++) target = deleteLastWord(target);
        frames.push({ text, delay: CFG.pauseBeforeDelete, kind: 'pause' });
        text = emitDeletes(text, target.length);
        frames.push({ text, delay: CFG.pauseBeforeRetype, kind: 'pause' });
        break;
      }

      case 'wait':
        frames.push({ text, delay: seg.ms === -1 ? CFG.waitDefault : seg.ms, kind: 'pause' });
        break;
    }
  }

  frames.push({ text, delay: CFG.pauseAfterTyped, kind: 'hold' });
  emitDeletes(text, 0);

  return frames;
}

function shuffle<T>(arr: readonly T[]): T[] {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

interface IdleDisplayProps {
  lines?: readonly string[];
  speed?: number;
  deleteSpeed?: number;
  pauseSpeed?: number;
  holdDelay?: number;
  lineDelay?: number;
  className?: string;
}

export const IdleDisplay = memo(function IdleDisplay({
  lines: linesProp,
  speed = 1,
  deleteSpeed = 1,
  pauseSpeed = 1,
  holdDelay,
  lineDelay = CFG.pauseAfterLine,
  className,
}: IdleDisplayProps) {
  const [lines] = useState(() => shuffle(linesProp ?? DEFAULT_LINES));
  const [displayed, setDisplayed] = useState('');
  const cfgRef = useRef({ speed, deleteSpeed, pauseSpeed, holdDelay, lineDelay });
  cfgRef.current = { speed, deleteSpeed, pauseSpeed, holdDelay, lineDelay };

  const lineIdx = useRef(0);
  const frameIdx = useRef(0);
  const frames = useRef<Frame[]>([]);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    function step() {
      if (frames.current.length === 0) {
        frames.current = buildFrames(lines[lineIdx.current % lines.length]);
        frameIdx.current = 0;
      }

      const frame = frames.current[frameIdx.current];
      if (!frame) {
        lineIdx.current++;
        frames.current = [];
        setDisplayed('');
        timer.current = setTimeout(step, cfgRef.current.lineDelay);
        return;
      }

      let delay: number;
      if (frame.kind === 'hold' && cfgRef.current.holdDelay !== undefined) {
        delay = cfgRef.current.holdDelay;
      } else if (frame.kind === 'hold') {
        delay = frame.delay;
      } else if (frame.kind === 'delete') {
        delay = frame.delay / cfgRef.current.deleteSpeed;
      } else if (frame.kind === 'pause') {
        delay = frame.delay / cfgRef.current.pauseSpeed;
      } else {
        delay = frame.delay / cfgRef.current.speed;
      }

      setDisplayed(frame.text);
      frameIdx.current++;
      timer.current = setTimeout(step, delay);
    }

    step();
    return () => clearTimeout(timer.current);
  }, [lines]);

  const showCursor = displayed.length > 0;

  return (
    <div className={className ?? 'flex items-start w-full min-w-0 leading-6'}>
      <span className="text-muted-foreground/60">
        {displayed}
        {showCursor && (
          <span className="inline-block w-[1px] h-[14px] bg-muted-foreground/40 ml-px translate-y-[1.5px] animate-cursor-blink" />
        )}
      </span>
    </div>
  );
});
